import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Spin, Button } from 'antd';
import {
  TeamOutlined,
  SafetyOutlined,
  UserOutlined,
  FileTextOutlined,
  ArrowUpOutlined,
  HomeOutlined,
  SettingOutlined,
  BellOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getCampusList } from '../../services/campus';
import { getAdminList } from '../../services/admin';
import { getStudentStatistics } from '../../services/student';
import './Dashboard.css';

const AdminDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState({
    campusCount: 0,
    adminCount: 0,
    studentCount: 0,
    gradeCount: 0,
  });

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const campusRes = await getCampusList({ pageSize: 1 });
      const adminRes = await getAdminList({ pageSize: 1 });
      const studentRes = await getStudentStatistics();
      
      setStatistics({
        campusCount: campusRes.total || 0,
        adminCount: adminRes.total || 0,
        studentCount: studentRes.total || 0,
        gradeCount: 0,
      });
    } catch (error) {
      console.error('获取统计数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: '校区数量',
      value: statistics.campusCount,
      icon: <HomeOutlined />,
      color: 'primary',
      path: '/admin/campuses',
      trend: '+12%',
    },
    {
      title: '管理员账号',
      value: statistics.adminCount,
      icon: <SafetyOutlined />,
      color: 'success',
      path: '/admin/admins',
      trend: '+5%',
    },
    {
      title: '学生总数',
      value: statistics.studentCount,
      icon: <UserOutlined />,
      color: 'warning',
      path: '/admin/students',
      trend: '+23%',
    },
    {
      title: '成绩记录',
      value: statistics.gradeCount,
      icon: <FileTextOutlined />,
      color: 'info',
      path: '/admin/grades',
      trend: '+8%',
    },
  ];

  const quickActions = [
    { title: '添加校区', icon: <HomeOutlined />, path: '/admin/campuses', color: '#4F46E5' },
    { title: '创建账号', icon: <SafetyOutlined />, path: '/admin/admins', color: '#10B981' },
    { title: '导入学生', icon: <UserOutlined />, path: '/admin/students', color: '#EA580C' },
    { title: '系统设置', icon: <SettingOutlined />, path: '/admin/settings', color: '#64748B' },
  ];

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* 页面标题 */}
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">控制台</h1>
          <p className="dashboard-subtitle">欢迎回来，查看系统整体运行状况</p>
        </div>
        <Button 
          type="primary" 
          icon={<BellOutlined />}
          className="dashboard-notify-btn"
        >
          系统通知
        </Button>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[24, 24]} className="dashboard-stats-row">
        {statCards.map((card, index) => (
          <Col xs={24} sm={12} lg={6} key={index}>
            <Card 
              className={`stat-card stat-card-${card.color}`}
              onClick={() => navigate(card.path)}
              hoverable
            >
              <div className="stat-card-content">
                <div className={`stat-card-icon stat-card-icon-${card.color}`}>
                  {card.icon}
                </div>
                <div className="stat-card-info">
                  <div className="stat-card-title">{card.title}</div>
                  <div className="stat-card-value">{card.value}</div>
                  <div className="stat-card-trend">
                    <ArrowUpOutlined /> {card.trend}
                    <span className="stat-card-trend-label">较上月</span>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      {/* 快捷操作 */}
      <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
        <Col xs={24} lg={16}>
          <Card className="dashboard-card" title="快捷操作">
            <div className="quick-actions">
              {quickActions.map((action, index) => (
                <div 
                  key={index} 
                  className="quick-action-item"
                  onClick={() => navigate(action.path)}
                >
                  <div 
                    className="quick-action-icon"
                    style={{ backgroundColor: `${action.color}15`, color: action.color }}
                  >
                    {action.icon}
                  </div>
                  <span className="quick-action-title">{action.title}</span>
                </div>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card className="dashboard-card" title="系统公告">
            <div className="announcement-list">
              <div className="announcement-item">
                <div className="announcement-dot" />
                <div className="announcement-content">
                  <div className="announcement-title">系统升级完成</div>
                  <div className="announcement-time">2026-03-22</div>
                </div>
              </div>
              <div className="announcement-item">
                <div className="announcement-dot" />
                <div className="announcement-content">
                  <div className="announcement-title">新增成绩审核功能</div>
                  <div className="announcement-time">2026-03-20</div>
                </div>
              </div>
              <div className="announcement-item">
                <div className="announcement-dot" />
                <div className="announcement-content">
                  <div className="announcement-title">支持多校区管理</div>
                  <div className="announcement-time">2026-03-18</div>
                </div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AdminDashboard;
